<template>
  <div class="flex flex-col items-center justify-center w-[100%] relative">
    <b-dropdown
      variant="link"
      toggle-class="!p-[0px] text-decoration-none shadow-[none] !rounded-[12px]"
      menu-class="!p-[4px] !rounded-[12px] !shadow-[0px_4px_12px_0px_#0000001A] !min-w-[150px] !border !border-[#E5E5E5]"
      no-caret
      :disabled="isLoading || isDisabled"
      :popper-opts="{ positionFixed: true }"
      boundary="window"
    >
      <template #button-content>
        <div
          class="flex items-center justify-between px-[12px] py-[6px] rounded-[12px] w-[120px] border-[1px]"
          :class="[
            getStatusBgClass(currentStatus),
            isError ? 'border-danger' : 'border-[transparent]',
            isLoading ? 'opacity-[0.7] cursor-wait' : 'cursor-pointer'
          ]"
        >
          <div
            v-if="isLoading"
            class="flex items-center justify-center w-[100%]"
          >
            <b-spinner
              small
              :class="getStatusTextClass(currentStatus)"
            />
          </div>
          <div
            v-else
            class="flex items-center justify-between w-[100%]"
          >
            <div class="flex items-center gap-[6px]">
              <span
                class="w-[6px] h-[6px] rounded-[50%] bg-[#F5F5F5]"
                :class="getStatusDotClass(currentStatus)"
              />
              <span
                class="text-[14px] font-medium"
                :class="getStatusTextClass(currentStatus)"
              >{{ getStatusText(currentStatus) }}</span>
            </div>
            <feather-icon
              icon="ChevronDownIcon"
              size="14"
              class="text-[#0A0A0A]"
            />
          </div>
        </div>
      </template>
      <b-dropdown-item
        :link-class="[
          '!px-[12px] !py-[8px] !text-[#0A0A0A] !w-full !rounded-[8px]',
          isStatusActive('publish') ? '!bg-[#F0FDF4]' : 'hover:!bg-[#F9F9F9]'
        ]"
        class="mx-[4px] my-[2px]"
        @click="updateStatus('publish')"
      >
        <div class="flex items-center justify-between w-full">
          <div class="flex items-center gap-[12px]">
            <span class="block flex-shrink-0 w-[8px] h-[8px] rounded-[50%] bg-[#22C55E]" />
            <span
              class="text-[14px]"
              :class="isStatusActive('publish') ? 'font-medium' : ''"
            >Publish</span>
          </div>
          <feather-icon
            v-if="isStatusActive('publish')"
            icon="CheckIcon"
            size="14"
            class="text-[#0A0A0A]"
          />
        </div>
      </b-dropdown-item>
      <b-dropdown-item
        :link-class="[
          '!px-[12px] !py-[8px] !text-[#0A0A0A] !w-full !rounded-[8px]',
          isStatusActive('draft') ? '!bg-[#F5F5F5]' : 'hover:!bg-[#F9F9F9]'
        ]"
        class="mx-[4px] my-[2px]"
        @click="updateStatus('draft')"
      >
        <div class="flex items-center justify-between w-full">
          <div class="flex items-center gap-[12px]">
            <span class="block flex-shrink-0 w-[8px] h-[8px] rounded-[50%] bg-[#737373]" />
            <span
              class="text-[14px]"
              :class="isStatusActive('draft') ? 'font-medium' : ''"
            >Draft</span>
          </div>
          <feather-icon
            v-if="isStatusActive('draft')"
            icon="CheckIcon"
            size="14"
            class="text-[#0A0A0A]"
          />
        </div>
      </b-dropdown-item>
      <b-dropdown-item
        :link-class="[
          '!px-[12px] !py-[8px] !text-[#0A0A0A] !w-full !rounded-[8px]',
          isStatusActive('hide') ? '!bg-[#FFE8D9]' : 'hover:!bg-[#F9F9F9]'
        ]"
        class="mx-[4px] my-[2px]"
        @click="updateStatus('hide')"
      >
        <div class="flex items-center justify-between w-full">
          <div class="flex items-center gap-[12px]">
            <span class="block flex-shrink-0 w-[8px] h-[8px] rounded-[50%] bg-[#F95E16]" />
            <span
              class="text-[14px]"
              :class="isStatusActive('hide') ? 'font-medium' : ''"
            >Hide</span>
          </div>
          <feather-icon
            v-if="isStatusActive('hide')"
            icon="CheckIcon"
            size="14"
            class="text-[#0A0A0A]"
          />
        </div>
      </b-dropdown-item>
    </b-dropdown>
  </div>
</template>

<script>
export default {
  name: 'ArtikelStatusDropdown',
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isDisabled: false,
      isError: false,
      currentStatus: this.item.status,
    }
  },
  watch: {
    // Keep the local status in sync when the parent mutates item.status (e.g. after a bulk operation)
    'item.status': {
      handler(newVal) {
        this.currentStatus = newVal
      },
    },
  },
  methods: {
    /**
     * Returns true when the given status key matches the current article status.
     * Handles legacy API values ('aktif' → publish, 'nonaktif' → hide).
     * @param {'publish'|'draft'|'hide'} statusKey
     * @returns {boolean}
     */
    isStatusActive(statusKey) {
      if (statusKey === 'publish') return this.currentStatus === 'publish' || this.currentStatus === 'aktif'
      if (statusKey === 'hide') return this.currentStatus === 'hide' || this.currentStatus === 'nonaktif'
      return this.currentStatus === statusKey
    },
    /**
     * Returns the display label for a given status value.
     * Maps legacy API values to their canonical labels.
     * @param {string} status
     * @returns {'Publish'|'Hide'|'Draft'}
     */
    getStatusText(status) {
      if (status === 'aktif' || status === 'publish') return 'Publish'
      if (status === 'nonaktif' || status === 'hide') return 'Hide'
      return 'Draft'
    },
    /** Returns the background CSS class for the status badge @param {string} status */
    getStatusBgClass(status) {
      if (status === 'aktif' || status === 'publish') return 'bg-[#F0FDF4]'
      if (status === 'nonaktif' || status === 'hide') return 'bg-[#FFE8D9]'
      return 'bg-[#F5F5F5]'
    },
    /** Returns the dot indicator CSS class for the given status @param {string} status */
    getStatusDotClass(status) {
      if (status === 'aktif' || status === 'publish') return 'bg-[#22C55E]'
      if (status === 'nonaktif' || status === 'hide') return 'bg-[#F95E16]'
      return 'bg-[#737373]'
    },
    /** Returns the text colour CSS class for the given status @param {string} status */
    getStatusTextClass(status) {
      if (status === 'aktif' || status === 'publish') return 'text-[#22C55E]'
      if (status === 'nonaktif' || status === 'hide') return 'text-[#F95E16]'
      return 'text-[#737373]'
    },
    /**
     * Optimistically updates the UI status, then persists the change via API.
     * Rolls back to the previous status and shows a toast on failure.
     * @param {'publish'|'draft'|'hide'} newStatus
     */
    async updateStatus(newStatus) {
      if (this.currentStatus === newStatus || this.isLoading) return

      const previousStatus = this.currentStatus
      // Optimistic update: change the UI immediately before the request completes
      this.currentStatus = newStatus
      this.isLoading = true

      try {
        await this.$http_new.patch(`/live-chat/api/v1/articles/${this.item.id}/status`, { status: newStatus })

        // On success, sync the status back onto the parent item object
        this.$toast_success({ message: `Status artikel berhasil diubah menjadi ${this.getStatusText(newStatus)}` })
        this.item.status = newStatus
        this.$emit('update-status', this.item)
      } catch (error) {
        this.currentStatus = previousStatus
        this.$toast_error({ message: 'Gagal update status' })
      } finally {
        this.isLoading = false
      }
    },
    /**
     * Externally controlled loading state, used by ArticleGroup during bulk status updates.
     * When a new status is provided it is also applied to the item and emitted upward.
     * @param {boolean} value
     * @param {string} [status]
     */
    setLoading(value, status) {
      this.isLoading = value
      if (!status) return
      this.item.status = status
      this.$emit('update-status', this.item)
    },
    /**
     * Enables or disables the dropdown trigger from the outside.
     * Called by ArticleGroup via a template ref during selection operations.
     * @param {boolean} value
     */
    setDisabled(value) {
      this.isDisabled = value
    },
  },
}
</script>
